import React from "react";
import { Button, Box, DialogContent, DialogActions } from "@mui/material";
import SbuxDialog2 from "../../../../../components/SbuxDialog2";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import SbuxSelect from "../../../../../components/SbuxSelect";
import type { MenuItem } from "../../../../../components/SbuxSelect";
import SbuxTextField from "../../../../../components/SubxTextField";
import useCss from "../../../../../hooks/useCss";
import {
  validateResourceForm,
  validateResourceNameFormat,
  validateResourceUrlFormat,
  checkDuplicateResourceName,
  checkDuplicateResourceUrl,
  getUniqueLobs,
  getFilteredNames,
  type ResourceGroup,
  type ResourceData,
} from "./utils";
import styles from "./styles";

interface RowData {
  instanceId?: string;
  lob?: string;
  name?: string;
  lobName?: string;
  resourceName?: string;
  resourceUrl?: string;
  uiPosition?: string | number;
  [key: string]: unknown;
}

interface Props {
  open: boolean;
  toggleDialogOpen: () => void;
  row: RowData;
  isAddNew: boolean;
  handleSave: (payload: RowData) => void;
  groupOptions?: ResourceGroup[];
  existingResources?: ResourceGroup[] | ResourceData;
}

const ResourceDialog = ({
  open,
  toggleDialogOpen,
  row,
  isAddNew,
  handleSave,
  groupOptions = [],
  existingResources = [],
}: Props) => {
  const classes = useCss(styles);
  const [formData, setFormData] = React.useState({
    instanceId: row?.instanceId || "",
    lob: row?.lob || "",
    name: row?.name || "",
    lobName: row?.lobName || "",
    resourceName: row?.resourceName || "",
    resourceUrl: row?.resourceUrl || "",
    uiPosition: String(row?.uiPosition ?? "2"),
  });

  const [fieldErrors, setFieldErrors] = React.useState({
    name: "",
    resourceName: "",
    resourceUrl: "",
    lob: "",
  });

  const urlDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  React.useEffect(() => {
    return () => {
      if (urlDebounceRef.current) clearTimeout(urlDebounceRef.current);
    };
  }, []);

  const resourceGroups = React.useMemo(() => {
    if (Array.isArray(existingResources)) return existingResources;
    const rd = existingResources as ResourceData;
    if (rd && Array.isArray(rd.data)) {
      return rd.data;
    }
    return [];
  }, [existingResources]);

  React.useEffect(() => {
    if (open) {
      setFormData({
        instanceId: row?.instanceId || "",
        lob: row?.lob || "",
        name: row?.name || "",
        lobName: row?.lobName || "",
        resourceName: row?.resourceName || "",
        resourceUrl: row?.resourceUrl || "",
        uiPosition: String(row?.uiPosition ?? "2"),
      });
      setFieldErrors({
        name: "",
        resourceName: "",
        resourceUrl: "",
        lob: "",
      });
    }
  }, [open, row]);

  const handleLobChange = (value: string) => {
    const updatedFormData = {
      ...formData,
      lob: value,
      name: "",
    };
    setFormData(updatedFormData);
    setFieldErrors({
      lob: "",
      name: "",
      resourceName: "",
      resourceUrl: "",
    });
  };

  const handleNameChange = (value: string) => {
    const selectedOption = groupOptions.find(
      (g) => g.lob === formData.lob && g.name === value,
    );
    if (selectedOption) {
      const updatedFormData = {
        ...formData,
        name: value,
        lobName: selectedOption.lobName || "",
        instanceId: selectedOption.instanceId || "",
        uiPosition: String(selectedOption.uiPosition ?? "2"),
      };
      setFormData(updatedFormData);

      let nextErrors = { ...fieldErrors, name: "" };

      const trimmedResName = (formData.resourceName || "").trim();
      if (trimmedResName) {
        const nameFormatError = validateResourceNameFormat(trimmedResName);
        nextErrors.resourceName =
          nameFormatError ||
          checkDuplicateResourceName(
            trimmedResName,
            updatedFormData,
            resourceGroups,
            isAddNew,
            row,
          );
      } else {
        nextErrors.resourceName = "";
      }

      const trimmedResUrl = (formData.resourceUrl || "").trim();
      if (trimmedResUrl) {
        const urlFormatError = validateResourceUrlFormat(trimmedResUrl);
        nextErrors.resourceUrl =
          urlFormatError ||
          checkDuplicateResourceUrl(
            trimmedResUrl,
            updatedFormData,
            resourceGroups,
            isAddNew,
            row,
          );
      } else {
        nextErrors.resourceUrl = "";
      }

      setFieldErrors(nextErrors);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    if (name === "resourceName") {
      let errorMsg = "";
      if (value && value.trim() !== "") {
        errorMsg = validateResourceNameFormat(value);
        if (!errorMsg && updatedFormData.lob && updatedFormData.name) {
          errorMsg = checkDuplicateResourceName(
            value,
            updatedFormData,
            resourceGroups,
            isAddNew,
            row,
          );
        }
      }
      setFieldErrors((prev) => ({ ...prev, resourceName: errorMsg }));
    } else if (name === "resourceUrl") {
      // Clear error immediately; validate after user stops typing
      setFieldErrors((prev) => ({ ...prev, resourceUrl: "" }));
      if (urlDebounceRef.current) clearTimeout(urlDebounceRef.current);
      urlDebounceRef.current = setTimeout(() => {
        let errorMsg = "";
        if (value && value.trim() !== "") {
          errorMsg = validateResourceUrlFormat(value);
          if (!errorMsg && updatedFormData.lob && updatedFormData.name) {
            errorMsg = checkDuplicateResourceUrl(
              value,
              updatedFormData,
              resourceGroups,
              isAddNew,
              row,
            );
          }
        }
        setFieldErrors((prev) => ({ ...prev, resourceUrl: errorMsg }));
      }, 500);
    } else {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const uniqueLobs = getUniqueLobs(groupOptions);
  const filteredNames = getFilteredNames(groupOptions, formData.lob);

  const lobMenuItems: MenuItem[] = uniqueLobs.map((lob) => ({
    name: lob,
    value: lob,
  }));
  const nameMenuItems: MenuItem[] = filteredNames.map((name) => ({
    name: name ?? "",
    value: name ?? "",
  }));

  const handleSubmit = () => {
    const allErrors = validateResourceForm(
      formData,
      resourceGroups,
      isAddNew,
      row,
    );

    if (
      allErrors.name ||
      allErrors.resourceName ||
      allErrors.resourceUrl ||
      allErrors.lob
    ) {
      setFieldErrors(allErrors);
      return;
    }

    const payload: RowData = {
      instanceId: formData.instanceId,
      lob: formData.lob,
      lobName: formData.lobName,
      name: formData.name,
      resourceName: formData.resourceName,
      resourceUrl: formData.resourceUrl,
      uiPosition: formData.uiPosition,
    };
    handleSave(payload);
    setFieldErrors({ lob: "", name: "", resourceName: "", resourceUrl: "" });
    toggleDialogOpen();
  };

  const isFormValid = React.useMemo(() => {
    const hasAllFields =
      (isAddNew ? formData.lob : true) &&
      formData.name &&
      formData.resourceName &&
      formData.resourceUrl;

    if (!hasAllFields) return false;

    const hasFieldErrors =
      fieldErrors.lob ||
      fieldErrors.name ||
      fieldErrors.resourceName ||
      fieldErrors.resourceUrl;

    return !hasFieldErrors;
  }, [formData, fieldErrors, isAddNew]);

  const dialogConfig = {
    title: isAddNew ? (
      <SbuxTranslate>{"Add New Resource"}</SbuxTranslate>
    ) : (
      <SbuxTranslate>{"Edit Resource"}</SbuxTranslate>
    ),
  };

  return (
    <SbuxDialog2
      open={open}
      config={dialogConfig}
      handleOnClose={toggleDialogOpen}
    >
      <DialogContent dividers>
        {isAddNew ? (
          <>
            <Box className={classes.dialogFormBox}>
              <SbuxSelect
                label={<SbuxTranslate>{"Line of Business"}</SbuxTranslate>}
                menuItems={lobMenuItems}
                defaultValue={formData.lob}
                value={formData.lob}
                handleSelectChange={handleLobChange}
                required
                autoFocus
              />
            </Box>
            <Box className={classes.dialogFormBox}>
              <SbuxSelect
                label={<SbuxTranslate>{"Name"}</SbuxTranslate>}
                menuItems={nameMenuItems}
                defaultValue={formData.name}
                value={formData.name}
                handleSelectChange={handleNameChange}
                required
                disabled={!formData.lob}
              />
            </Box>
          </>
        ) : (
          <>
            <Box className={classes.dialogFormBox}>
              <SbuxTextField
                label={<SbuxTranslate>{"Line of Business"}</SbuxTranslate>}
                name="lob"
                value={formData.lob}
                disabled
                preventAutoFocus
              />
            </Box>
            <Box className={classes.dialogFormBox}>
              <SbuxTextField
                label={<SbuxTranslate>{"Name"}</SbuxTranslate>}
                name="name"
                value={formData.name}
                disabled
                preventAutoFocus
              />
            </Box>
          </>
        )}
        <Box className={classes.dialogFormBox}>
          <SbuxTextField
            label={<SbuxTranslate>{"Resource Name"}</SbuxTranslate>}
            name="resourceName"
            value={formData.resourceName}
            handleChange={handleChange}
            required
            validateFirstCharacter={false}
            preventAutoFocus={isAddNew}
            isError={!!fieldErrors.resourceName}
            errorMessage={
              fieldErrors.resourceName ? (
                <SbuxTranslate>{fieldErrors.resourceName}</SbuxTranslate>
              ) : (
                " "
              )
            }
          />
        </Box>
        <Box className={classes.dialogFormBox}>
          <SbuxTextField
            label={<SbuxTranslate>{"Resource URL"}</SbuxTranslate>}
            name="resourceUrl"
            value={formData.resourceUrl}
            handleChange={handleChange}
            required
            preventAutoFocus
            isError={!!fieldErrors.resourceUrl}
            errorMessage={
              fieldErrors.resourceUrl ? (
                <SbuxTranslate>{fieldErrors.resourceUrl}</SbuxTranslate>
              ) : (
                " "
              )
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Box className="actionContainer">
          <Button
            variant="contained"
            size="medium"
            onClick={handleSubmit}
            disabled={!isFormValid}
          >
            <SbuxTranslate>{"Save"}</SbuxTranslate>
          </Button>
          <Button variant="outlined" size="medium" onClick={toggleDialogOpen}>
            <SbuxTranslate>{"Cancel"}</SbuxTranslate>
          </Button>
        </Box>
      </DialogActions>
    </SbuxDialog2>
  );
};

export default ResourceDialog;
